import {
  CalendarCheckIcon,
  CheckIcon,
  FlexBox,
  HandshakeAngleIcon,
  JoonColorExpanded,
  JoonUIColor,
  LocationArrowIcon,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useEffect } from "react"
import { Trans, useTranslation } from "react-i18next"

import ReadyButton from "./ReadyButton"
import PageWrapper from "../../../components/pageWrapper/PageWrapper"
import useDocumentTitle from "../../../hooks/useDocumentTitle"
import useIsCoachingAvailable from "../../../hooks/useIsCoachingAvailable"
import useMobile from "../../../hooks/useMobile"
import { CoachingStep } from "../../../types/coachingGroups"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { requireAuth } from "../../../util/auth"
import { useRouter } from "../../../util/router"
import useCareTeam from "../../care/useCareTeam"
import { useCoachingStore } from "../useCoachingStore"

const CareMarketingPage = () => {
  useDocumentTitle("Joon - Care")
  const { setStep } = useCoachingStore()
  const { careTeam, isLoading, hasCoach, firstCoach } = useCareTeam()
  const isMobile = useMobile()
  const router = useRouter()
  const { t } = useTranslation()
  const isCoachingAvailable = useIsCoachingAvailable()

  const onReadyButtonClick = () => {
    setStep(CoachingStep.LANDING)
    router.push("/coaching?from=preview")
  }

  useEffect(() => {
    if (hasCoach) router.push(`/coach/${firstCoach?.user.id}`)
    // This page is the coaching storefront, so it's unreachable by nav for users
    // who can't buy a coach — bounce anyone who lands on the URL directly.
    else if (!isCoachingAvailable) router.push("/me")
  }, [careTeam, hasCoach, firstCoach, router, isCoachingAvailable])

  useEffect(() => {
    if (isLoading || hasCoach || !isCoachingAvailable) return
    trackAnalyticEvent(ANALYTIC_EVENTS.MARKETING_TAB_VIEWED)
  }, [isLoading, hasCoach, isCoachingAvailable])

  if (isLoading || hasCoach || !isCoachingAvailable) return <></>

  return (
    <PageWrapper
      style={{
        background: JoonColorExpanded.yellow100,
        paddingBottom: "0px",
      }}
    >
      <FlexBox
        direction="column"
        align="center"
        wrap={false}
        style={{
          position: "relative",
          padding: SPACING.space0,
          paddingTop: SPACING.space9,
          width: "min(600px, 100%)",
        }}
        gap={SPACING.space9}
      >
        <FlexBox direction="column" align="center" gap={SPACING.space4}>
          <Typography variant="bodySmall" textAlign="center">
            {t("COACH_PAYWALL_FINALLY")}
          </Typography>
          <Typography variant="h1" textAlign="center" style={{ width: "75%" }}>
            {t("COACH_PAYWALL_HEADLINE")}
          </Typography>
          <Typography
            variant="body"
            textAlign="center"
            style={{ width: "min(400px, 85%)" }}
          >
            <Trans
              i18nKey="COACH_PAYWALL_SUBTEXT"
              components={{
                b: <Typography variant="bodyBold">{""}</Typography>,
              }}
            />
          </Typography>
          <img
            src="/images/guidePhotos/coach_main_headline.svg"
            style={{ width: "min(380px, 80%)" }}
            alt="<3"
          />
        </FlexBox>

        <img src="/images/squiggle.svg" style={{ width: "70%" }} alt="~~~" />

        <Typography variant="h2" textAlign="center" style={{ width: "75%" }}>
          {t("COACH_PAYWALL_WHY_TITLE")}
        </Typography>
        <FlexBox
          direction="column"
          align="center"
          gap={SPACING.space12}
          style={{ marginBottom: SPACING.space4 }}
        >
          <FlexBox direction="column" align="center">
            <Typography
              variant="bodyBold"
              textAlign="center"
              style={{ width: "min(400px, 85%)" }}
            >
              {t("COACH_PAYWALL_BENEFIT1_TITLE")}
            </Typography>
            <img
              src="/images/guidePhotos/coach_practical_strategy.svg"
              style={{
                width: "min(400px, 80%)",
                paddingBottom: SPACING.space3,
              }}
              alt="Practical strategies"
            />
            <Typography
              variant="bodySmall"
              textAlign="center"
              style={{ width: "min(400px, 85%)" }}
            >
              {t("COACH_PAYWALL_BENEFIT1_DESC")}
            </Typography>
          </FlexBox>
          <FlexBox direction="column" align="center">
            <Typography variant="bodyBold" textAlign="center">
              {t("COACH_PAYWALL_BENEFIT2_TITLE")}
            </Typography>
            <img
              src="/images/guidePhotos/parent_task.svg"
              style={{ width: "min(400px, 80%)", paddingTop: SPACING.space3 }}
              alt="Practical strategies"
            />
            <Typography
              variant="bodySmall"
              textAlign="center"
              style={{ width: "min(400px, 85%)" }}
            >
              {t("COACH_PAYWALL_BENEFIT2_DESC")}
            </Typography>
          </FlexBox>
          <FlexBox direction="column" align="center">
            <Typography
              variant="bodyBold"
              textAlign="center"
              style={{ width: "min(400px, 85%)" }}
            >
              {t("COACH_PAYWALL_BENEFIT3_TITLE")}
            </Typography>
            <img
              src="/images/guidePhotos/zoom.svg"
              style={{
                width: "min(400px, 80%)",
                paddingTop: SPACING.space3,
                paddingBottom: SPACING.space2,
              }}
              alt="Meet Your Coach"
            />
            <Typography
              variant="bodySmall"
              textAlign="center"
              style={{ width: "min(400px, 70%)" }}
            >
              {t("COACH_PAYWALL_BENEFIT3_DESC")}
            </Typography>
          </FlexBox>
          <FlexBox direction="column" align="center">
            <Typography variant="bodyBold" textAlign="center">
              {t("COACH_PAYWALL_BENEFIT4_TITLE")}
            </Typography>
            <img
              src="/images/guidePhotos/learning_modules.svg"
              style={{
                width: "min(400px, 80%)",
              }}
              alt="Learning Modules"
            />
            <Typography
              variant="bodySmall"
              textAlign="center"
              style={{ width: "min(400px, 70%)" }}
            >
              {t("COACH_PAYWALL_BENEFIT4_DESC")}
            </Typography>
          </FlexBox>
        </FlexBox>

        <FlexBox
          direction="column"
          align="center"
          wrap={false}
          style={{ width: "min(500px, 100%)" }}
        >
          <FlexBox
            direction="column"
            align="center"
            gap={SPACING.space10}
            style={{
              background: JoonColorExpanded.purple600,
              padding: SPACING.space4,
              paddingBottom: SPACING.space12,
              borderTopLeftRadius: SPACING.space4,
              borderTopRightRadius: SPACING.space4,
            }}
          >
            <Typography
              variant="h2"
              textAlign="center"
              color={JoonUIColor.text.inverted}
              style={{ marginTop: SPACING.space4 }}
            >
              {t("COACH_PAYWALL_HOW_TITLE")}
            </Typography>
            <FlexBox direction="column" align="center" gap={SPACING.space2}>
              <HandshakeAngleIcon
                size={48}
                color={JoonColorExpanded.yellow400}
              />
              <Typography
                variant="bodyBold"
                textAlign="center"
                color={JoonUIColor.text.inverted}
                style={{ width: "min(400px, 70%)" }}
              >
                {t("COACH_PAYWALL_HOW1_TITLE")}
              </Typography>
              <Typography
                variant="bodySmall"
                textAlign="center"
                color={JoonUIColor.text.inverted}
                style={{ width: "min(400px, 70%)" }}
              >
                {t("COACH_PAYWALL_HOW1_DESC")}
              </Typography>
            </FlexBox>
            <FlexBox direction="column" align="center" gap={SPACING.space2}>
              <CalendarCheckIcon
                size={48}
                color={JoonColorExpanded.yellow400}
              />
              <Typography
                variant="bodyBold"
                textAlign="center"
                color={JoonUIColor.text.inverted}
              >
                {t("COACH_PAYWALL_HOW2_TITLE")}
              </Typography>
              <Typography
                variant="bodySmall"
                textAlign="center"
                color={JoonUIColor.text.inverted}
              >
                {t("COACH_PAYWALL_HOW2_DESC")}
              </Typography>
            </FlexBox>
            <FlexBox direction="column" align="center" gap={SPACING.space2}>
              <LocationArrowIcon
                size={48}
                color={JoonColorExpanded.yellow400}
              />
              <Typography
                variant="bodyBold"
                textAlign="center"
                color={JoonUIColor.text.inverted}
                style={{ width: "min(400px, 70%)" }}
              >
                {t("COACH_PAYWALL_HOW3_TITLE")}
              </Typography>
              <Typography
                variant="bodySmall"
                textAlign="center"
                color={JoonUIColor.text.inverted}
                style={{ width: "min(400px, 70%)" }}
              >
                {t("COACH_PAYWALL_HOW3_DESC")}
              </Typography>
            </FlexBox>
          </FlexBox>

          <FlexBox
            direction="column"
            align="center"
            gap={SPACING.space6}
            wrap={false}
            style={{
              height: isMobile ? "590px" : "480px",
              background: JoonColorExpanded.yellow400,
              padding: `${SPACING.space12} ${SPACING.space6}`,
            }}
          >
            <Typography
              variant="h2"
              textAlign="center"
              color={JoonUIColor.text.primary}
              style={{ width: "min(350px, 90%)" }}
            >
              {t("COACH_PAYWALL_PLAN_TITLE")}
            </Typography>

            <FlexBox
              direction="column"
              justify="left"
              gap={SPACING.space4}
              wrap={false}
              style={{
                width: "auto",
                borderRadius: SPACING.space4,
                padding: `${SPACING.space6} ${SPACING.space4}`,
                background: JoonColorExpanded.yellow100,
              }}
            >
              <FlexBox
                direction="row"
                gap={SPACING.space2}
                align="center"
                wrap={false}
              >
                <CheckIcon size={28} color={JoonColorExpanded.viridian400} />
                <Typography
                  variant="bodySmall"
                  textAlign="left"
                  style={{ width: "80%" }}
                >
                  {t("COACH_PAYWALL_PLAN_ITEM1")}
                </Typography>
              </FlexBox>
              <FlexBox
                direction="row"
                gap={SPACING.space2}
                align="center"
                wrap={false}
              >
                <CheckIcon size={28} color={JoonColorExpanded.viridian400} />
                <Typography variant="bodySmall" textAlign="left">
                  {t("COACH_PAYWALL_PLAN_ITEM2")}
                </Typography>
              </FlexBox>
              <FlexBox
                direction="row"
                gap={SPACING.space2}
                align="center"
                wrap={false}
              >
                <CheckIcon size={28} color={JoonColorExpanded.viridian400} />
                <Typography
                  variant="bodySmall"
                  textAlign="left"
                  style={{ width: "80%" }}
                >
                  {t("COACH_PAYWALL_PLAN_ITEM3")}
                </Typography>
              </FlexBox>
            </FlexBox>
          </FlexBox>
        </FlexBox>
        <ReadyButton
          onClick={onReadyButtonClick}
          style={{
            bottom: isMobile ? "76px" : "20px",
          }}
        />
      </FlexBox>
    </PageWrapper>
  )
}

const CareMarketingPageWithAuth = requireAuth(CareMarketingPage)
export default CareMarketingPageWithAuth
